import { useState } from "react";
import { Satellite, ZoomIn, ZoomOut, MapPin, Loader2, Search, Home, CheckCircle, Calendar, FileText, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ViewMyRoofSection() {
  const [address, setAddress] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mapLocation, setMapLocation] = useState({ lat: null, lng: null });
  const [mapUrl, setMapUrl] = useState(null);
  const [zoom, setZoom] = useState(20);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [showActions, setShowActions] = useState(false);

  // Fetch autocomplete suggestions
  const fetchSuggestions = async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: { input }
      });
      setSuggestions(response.data.predictions || []);
      setShowDropdown(true);
    } catch (error) {
      console.error("Autocomplete error:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  let debounceTimer;
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setAddress(newValue);
    
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion) => {
    setAddress(suggestion.description);
    setSelectedAddress(suggestion.description);
    setShowDropdown(false);
    setSuggestions([]);

    try {
      const response = await axios.get(`${API}/places/details`, {
        params: { place_id: suggestion.place_id }
      });
      
      if (response.data.lat && response.data.lng) {
        setMapLocation({ lat: response.data.lat, lng: response.data.lng });
        fetchMapUrl(response.data.lat, response.data.lng, zoom);
        setShowActions(true);
      }
    } catch (error) {
      console.error("Place details error:", error);
    }
  };

  const fetchMapUrl = async (lat, lng, zoomLevel) => {
    setIsMapLoading(true);
    try {
      const response = await axios.get(`${API}/maps/static`, {
        params: { lat, lng, zoom: zoomLevel }
      });
      setMapUrl(response.data.url);
    } catch (error) {
      console.error("Map URL error:", error);
    } finally {
      setIsMapLoading(false);
    }
  };

  const handleZoomIn = () => {
    const newZoom = Math.min(zoom + 1, 21);
    setZoom(newZoom);
    if (mapLocation.lat && mapLocation.lng) {
      fetchMapUrl(mapLocation.lat, mapLocation.lng, newZoom);
    }
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - 1, 17);
    setZoom(newZoom);
    if (mapLocation.lat && mapLocation.lng) {
      fetchMapUrl(mapLocation.lat, mapLocation.lng, newZoom);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="view-roof" className="py-20 md:py-28 bg-white" data-testid="view-roof-section">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-emerald-200">
            <Satellite className="w-4 h-4" />
            Satellite Technology
          </div>
          <h2 className="font-heading text-4xl md:text-5xl text-slate-900 mb-4">
            View Your Roof
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Enter your address to see a high-resolution satellite view of your roof. 
            Our technology helps identify potential issues before they become problems.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left - Address Input & Info */}
          <div className="space-y-6">
            {/* Address Input */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <label className="text-slate-800 font-medium mb-3 block">Enter Your Property Address</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10 pointer-events-none" />
                <input
                  type="text"
                  value={address}
                  onChange={handleInputChange}
                  onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                  placeholder="Start typing your address..."
                  className="w-full h-14 pl-12 pr-10 rounded-xl bg-white text-slate-800 placeholder-slate-400 border border-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-lg transition-colors"
                  data-testid="view-roof-address-input"
                  autoComplete="off"
                />
                {isLoading && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500 animate-spin" />
                )}
                
                {/* Dropdown */}
                {showDropdown && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={suggestion.place_id || index}
                        type="button"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors border-b border-slate-100 last:border-b-0"
                      >
                        <p className="text-slate-800 font-medium text-sm">{suggestion.main_text}</p>
                        <p className="text-slate-500 text-xs">{suggestion.secondary_text}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* What We Analyze */}
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
              <h3 className="text-slate-800 font-heading text-lg mb-4">What We'll Analyze:</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  "Roof Condition",
                  "Shingle Damage",
                  "Potential Leaks",
                  "Solar Compatibility",
                  "Storm Damage Signs",
                  "Age Assessment"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons - Show after address selected */}
            {showActions && (
              <div className="space-y-3 animate-fadeIn">
                <Button 
                  onClick={() => scrollToSection("booking")}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold py-5 text-base transition-all duration-300"
                  data-testid="request-inspection-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Request Professional Inspection
                </Button>
                <Button 
                  onClick={() => scrollToSection("upload-estimate")}
                  variant="outline"
                  className="w-full bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl font-semibold py-5 text-base transition-all duration-300"
                  data-testid="upload-estimate-btn"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Upload Insurance Estimate
                </Button>
                <Button 
                  onClick={() => scrollToSection("storm-center")}
                  variant="outline"
                  className="w-full bg-white border-2 border-amber-400 text-amber-700 hover:bg-amber-50 rounded-xl font-semibold py-5 text-base transition-all duration-300"
                  data-testid="storm-damage-btn"
                >
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Check for Storm Damage
                </Button>
              </div>
            )}
          </div>

          {/* Right - Satellite Map */}
          <div className="relative">
            {(!mapLocation.lat || !mapLocation.lng) ? (
              <div className="bg-slate-100 rounded-2xl h-[450px] flex flex-col items-center justify-center border border-slate-200">
                <div className="w-24 h-24 bg-slate-200 rounded-full flex items-center justify-center mb-6">
                  <Home className="w-12 h-12 text-slate-400" />
                </div>
                <p className="text-slate-700 text-center text-xl mb-2">
                  See Your Roof From Above
                </p>
                <p className="text-slate-500 text-center text-sm max-w-xs">
                  Enter your address to view a high-resolution satellite image of your property
                </p>
              </div>
            ) : (
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                {isMapLoading && (
                  <div className="absolute inset-0 bg-white/90 flex items-center justify-center z-10">
                    <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
                  </div>
                )}
                
                {mapUrl && (
                  <img 
                    src={mapUrl} 
                    alt="Satellite view of your property"
                    className="w-full h-[450px] object-cover"
                    data-testid="view-roof-map-image"
                  />
                )}
                
                {/* Zoom Controls */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  <button
                    onClick={handleZoomIn}
                    className="bg-white hover:bg-slate-50 p-3 rounded-lg shadow-lg transition-colors border border-slate-200"
                    data-testid="view-roof-zoom-in"
                  >
                    <ZoomIn className="w-5 h-5 text-slate-700" />
                  </button>
                  <button
                    onClick={handleZoomOut}
                    className="bg-white hover:bg-slate-50 p-3 rounded-lg shadow-lg transition-colors border border-slate-200"
                    data-testid="view-roof-zoom-out"
                  >
                    <ZoomOut className="w-5 h-5 text-slate-700" />
                  </button>
                </div>

                {/* Roof Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 via-slate-900/90 to-transparent p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-xs text-emerald-400 font-medium uppercase tracking-wider">Your Property</p>
                      <p className="text-white font-semibold">{selectedAddress}</p>
                      <p className="text-slate-400 text-sm mt-1">
                        For accurate measurements, schedule a professional inspection
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        {!showActions && (
          <div className="mt-12 text-center">
            <p className="text-slate-500 mb-4">Don't have your address handy?</p>
            <Button 
              onClick={() => scrollToSection("booking")}
              variant="outline"
              className="bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full font-semibold px-8 py-4"
            >
              Schedule an Inspection Instead
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
